import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthToken, userAPI } from '../Services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshUser = async () => {
    if (!user?.token) return;
    try {
      const response = await userAPI.getMe();
      const me = response.data;
      if (me) {
        const updatedUser = {
          id: me.id,
          email: me.email,
          name: me.full_name,
          role: me.role,
          token: user.token,
          phone_number: me.phone_number
        };
        setUser(updatedUser);
        localStorage.setItem('meettrack_user', JSON.stringify(updatedUser));
      }
    } catch {}
  };

 useEffect(() => {
  const storedUser = localStorage.getItem('meettrack_user');
  if (storedUser) {
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setAuthToken(userData.token);
      // Validate token 
      authAPI.getSession().catch(() => {
        // Token invalid/expired — force logout
        localStorage.removeItem('meettrack_user');
        setUser(null);
        setAuthToken(null);
      });
    } catch {
      localStorage.removeItem('meettrack_user');
    }
  }
  setLoading(false);
}, []);

  useEffect(() => {
    const handler = (e) => {
      const updatedId = e.detail?.userId;
      if (updatedId && user?.id === updatedId) {
        refreshUser();
      }
    };
    window.addEventListener('userUpdated', handler);
    return () => {
      window.removeEventListener('userUpdated', handler);
    };
  }, [user?.id]);

  const login = async (email) => {
    try {
      setLoading(true);
      await authAPI.sendOTP(email);
    } catch (error) {
      // Extract error message from response
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to send OTP. Please try again.';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (email, otp) => {
    try {
      setLoading(true);
      const response = await authAPI.login(email, otp);
      
      if (response.data && response.data.user && response.data.token) {
        const userData = {
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.full_name,
          role: response.data.user.role,
          token: response.data.token.access_token,
          phone_number: response.data.user.phone_number
        };
        
        setUser(userData);
        localStorage.setItem('meettrack_user', JSON.stringify(userData));
        setAuthToken(userData.token); // Set token for API calls
        
        return response.data;
      } else {
        throw new Error(response.data?.detail || 'OTP verification failed');
      }
    } catch (error) {
      // Extract detailed error message from response
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'OTP verification failed. Please try again.';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const adminLogin = async (email, password) => {
    try {
      setLoading(true);
      
      const response = await authAPI.adminLogin(email, password);
      
      
      if (response.data && response.data.user && response.data.token) {
        const userData = {
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.full_name,
          role: response.data.user.role,
          token: response.data.token.access_token,
          phone_number: response.data.user.phone_number
        };
        
        
        setUser(userData);
        localStorage.setItem('meettrack_user', JSON.stringify(userData));
        setAuthToken(userData.token); // Set token for API calls
        
        
        
        // Return the user data for immediate use
        return userData;
      } else {
        throw new Error(response.data?.detail || 'Login failed');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      // Extract detailed error message from response
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Invalid credentials. Please check your email and password.';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    setUser(null);
    localStorage.removeItem('meettrack_user');
    setAuthToken(null); // Clear token
  };

 

  const value = {
    user,
    refreshUser,
    login,
    verifyOTP,
    adminLogin,
    logout,

    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
